import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Check } from 'lucide-react';

/**
 * PWA Install Button Component
 * يظهر زر تثبيت التطبيق عندما يكون متاحاً
 */
export default function PWAInstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show install banner after 3 seconds if not dismissed before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowInstallBanner(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show anything if already installed
  if (isInstalled) {
    return null;
  }

  // Show nothing if not installable (except iOS)
  if (!deferredPrompt && !isIOS) {
    return null;
  }

  return (
    <>
      {/* زر التثبيت الثابت */}
      <button
        onClick={handleInstallClick}
        className="fixed bottom-28 left-4 z-50 flex items-center gap-2 px-4 py-3 bg-gold text-primary rounded-full shadow-lg hover:bg-gold-dark transition-all animate-bounce"
        data-testid="pwa-install-btn"
        title="تثبيت التطبيق"
      >
        <Smartphone className="w-5 h-5" />
        <span className="font-bold text-sm">تثبيت التطبيق</span>
      </button>

      {/* بانر التثبيت */}
      {showInstallBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-primary text-white p-4 shadow-2xl animate-slide-up">
          <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gold rounded-xl flex items-center justify-center">
                <img src="/logo-dark.jpg" alt="Logo" className="w-10 h-10 rounded-lg" />
              </div>
              <div>
                <h4 className="font-bold text-sm">تثبيت التطبيق</h4>
                <p className="text-xs text-white/70">احصل على تجربة أفضل مع التطبيق</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleInstallClick}
                className="px-4 py-2 bg-gold text-primary rounded-lg font-bold text-sm hover:bg-gold-dark transition-all"
              >
                تثبيت
              </button>
              <button
                onClick={dismissBanner}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* تعليمات iOS */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden" dir="rtl">
            <div className="bg-primary text-white p-4 flex items-center justify-between">
              <h3 className="font-bold">تثبيت التطبيق على iPhone</h3>
              <button onClick={() => setShowIOSInstructions(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">1</div>
                <div>
                  <p className="font-semibold">اضغط على زر المشاركة</p>
                  <p className="text-sm text-gray-500">في أسفل الشاشة (Safari)</p>
                  <div className="mt-2 text-2xl">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">2</div>
                <div>
                  <p className="font-semibold">اختر "إضافة للشاشة الرئيسية"</p>
                  <p className="text-sm text-gray-500">Add to Home Screen</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                  <Check className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold">اضغط "إضافة"</p>
                  <p className="text-sm text-gray-500">سيظهر التطبيق على شاشتك الرئيسية</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t">
              <button
                onClick={() => setShowIOSInstructions(false)}
                className="w-full py-3 bg-primary text-white rounded-lg font-bold"
              >
                فهمت
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
